import { Box, Button, useSideSheetState } from '@bechtel/gds';
import { CommodityType } from '@bechtel/types';
import React, { FC, Suspense } from 'react';
import { useBlockPermission, ErrorBoundary, ActionContent, SuspenseLoader, WorkPackage3dViewer } from '@bechtel/client-common';
import { useWorkPackage } from '../context';
import { ButtonBlock, WorkPackageActionsContainer } from './styled';
import DocumentManager from '~/detail/document-manager/internals';
import { Materials } from '../materials/internals';
import { CreateBlock } from '@bechtel/client-blocks';
import { t } from '@lingui/macro';

interface WorkPackageActionsProps {
  commodityTypes?: CommodityType[];
}

export const WorkPackageActions: FC<WorkPackageActionsProps> = ({ commodityTypes }) => {
  const {
    visible: showDocumentManager,
    open: openDocumentManager,
    close: closeDocumentManager,
  } = useSideSheetState(false);
  const { visible: show3dViewer, open: open3dViewer, close: close3dViewer } = useSideSheetState(false);
  const { visible: showMaterialView, open: openMaterialView, close: closeMaterialView } = useSideSheetState(false);
  const { visible: showCreateBlock, open: openCreateBlock, close: closeCreateBlock } = useSideSheetState(false);
  const { assignedCrewMembers, id, tag: workPackageTag, facilities } = useWorkPackage();
  const { canCreateBlock } = useBlockPermission(assignedCrewMembers, facilities ? facilities : []);

  return (
    <WorkPackageActionsContainer>
      <Box mr={{ sm: '400' }} flexShrink={{ sm: '0' }}>
        <Button onClick={open3dViewer}>
          <ActionContent icon="cube" label={t`View 3D Model`} />
        </Button>
      </Box>
      <Box mr={{ sm: '400' }} flexShrink={{ sm: '0' }}>
        <Button onClick={openDocumentManager}>
          <ActionContent icon="ruler-combined" label={t`View Documents`} />
        </Button>
      </Box>
      <Box mr={{ sm: '400' }} flexShrink={{ sm: '0' }}>
        <Button onClick={openMaterialView}>
          <ActionContent icon="clipboard-list" label={t`View Materials`} />
        </Button>
      </Box>
      {canCreateBlock && commodityTypes && commodityTypes.length > 0 && (
        <Box flexShrink={{ sm: '0' }}>
          <ButtonBlock onClick={openCreateBlock}>
            <ActionContent icon="exclamation-triangle" label={t`Report Blocks`} />
          </ButtonBlock>
        </Box>
      )}
      {showDocumentManager && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <DocumentManager workPackageId={id} onClose={closeDocumentManager} />
          </Suspense>
        </ErrorBoundary>
      )}
      {show3dViewer && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WorkPackage3dViewer workPackageTag={workPackageTag} onClose={close3dViewer} />
          </Suspense>
        </ErrorBoundary>
      )}
      {showMaterialView && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <Materials onClose={closeMaterialView} />
          </Suspense>
        </ErrorBoundary>
      )}
      {showCreateBlock && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <CreateBlock
              workPackageId={id}
              workPackageTag={workPackageTag}
              assignedCrewMembers={assignedCrewMembers}
              facilities={facilities ? facilities : []}
              commodityTypes={commodityTypes}
              multiBlock={true}
              close={closeCreateBlock}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </WorkPackageActionsContainer>
  );
};
